import moment from 'moment';
import { globalTranslate } from '@pfsc/shared/locale/app.i18n';
import { makePlural } from './makePlural';
import { toReadableIfNull } from './toReadableIfNull';
import { getLanguagueFromUrl } from '../ui-services/language.service';
import { capitalizeFirstChar } from './capitializeText';

/**
 * Map date to readable
 * @param {Date | null} date Date
 * @returns {string | unknown}
 */
function toReadable(date) {
  if (date == null) {
    return toReadableIfNull(date);
  }
  return moment(date).format('MM/DD/YYYY');
}

/**
 * Map date to readable
 * @param {Date | null} date Date
 * @returns {string | unknown}
 */
function toReadableWithTime(date) {
  if (date == null) {
    return toReadableIfNull(date);
  }
  return moment(date).format('MM/DD/YYYY hh:mm A');
}

/**
 * Get weekday.
 * @param {Date | null} date Date.
 * @param {string} format Weekday format. Default: "dddd", ex: Wednesday.
 * @returns {string | unknown}
 */
function getWeekday(date, format = 'dddd') {
  if (date == null) {
    return toReadableIfNull(date);
  }
  const localeCode = getLanguagueFromUrl();
  return capitalizeFirstChar(moment(date).locale(localeCode).format(format));
}

/**
 * Get readable month.
 * @param {Date | null} date Date.
 * @param {string} format Month format. Default: "MMM", ex: Jan.
 * @returns {string | unknown}
 */
function getMonth(date, format = 'MMM') {
  if (date == null) {
    return toReadableIfNull(date);
  }
  const localeCode = getLanguagueFromUrl();
  return capitalizeFirstChar(moment(date).locale(localeCode).format(format));
}

/**
 * Get the UTC time (time with +0:00 timezone).
 * @returns {Date}
 */
function getUTCDate() {
  // The 60000 is used to convert minutes to milliseconds
  const timezoneOffsetInMilliseconds = new Date().getTimezoneOffset() * 60000;
  const utcTime = new Date().getTime() + timezoneOffsetInMilliseconds;
  return moment(utcTime).toDate();
}

/**
 * Get time duration.
 * @param {Date} startDate Start date.
 * @param {Date} endDate End date.
 * @returns {string}
 */
function getTimeDurationWithHourAndMinuteString(startDate, endDate) {
  const hasTimeRange = startDate != null && endDate != null;
  if (!hasTimeRange) {
    return '-';
  }

  const startTime = moment(startDate, 'HH:mm:ss');
  const endMoment = moment(endDate, 'HH:mm:ss');
  const duration = moment.duration(endMoment.diff(startTime));
  const hours = duration.hours();
  const minutes = duration.minutes();
  const hourPlural = makePlural(hours, { one: globalTranslate('time.hour'), other: globalTranslate('time.hours') });
  const minutePlural = makePlural(minutes, { one: globalTranslate('time.minute'), other: globalTranslate('time.minutes') });
  return `${hours} ${hourPlural} ${minutes} ${minutePlural}`;
}

/**
 * Convert time string to am/pm format.
 * @param {string | null} time Time string in 24h format (Ex: "18:00:00").
 * @returns {string}
 */
function formatTime(time) {
  if (time == null) {
    return toReadableIfNull(time);
  }
  return moment(time, 'HH:mm:ss').format('hh:mm A');
}

export const dateUtils = {
  getMonth,
  toReadable,
  toReadableWithTime,
  getWeekday,
  getTimeDurationWithHourAndMinuteString,
  getUTCDate,
  formatTime,
};
